// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { useSelector } from 'react-redux';
import { Col } from 'antd/lib/grid';
import Icon from '@ant-design/icons';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';

import {
    AnalysisIcon,
    FilterIcon, FullscreenIcon,
    // GuideIcon,
    InfoIcon,
    ShortcutIcon,
} from 'icons';
import { DimensionType } from 'cvat-core-wrapper';
import { CombinedState, GetWorkspaceChinese, Workspace } from 'reducers';

// import MDEditor from '@uiw/react-md-editor';

interface Props {
    workspace: Workspace;
    showStatistics(): void;
    showFilters(): void;
    changeWorkspace(workspace: Workspace): void;
    jobInstance: any;
}

function RightGroup(props: Props): JSX.Element {
    const {
        showStatistics,
        changeWorkspace,
        workspace,
        jobInstance,
        showFilters,
    } = props;

    const annotationFilters = useSelector((state: CombinedState) => state.annotation.annotations.filters);
    const filters = annotationFilters.length;

    return (
        <Col className='cvat-annotation-header-right-group'>
            <Button
                type='link'
                className='cvat-annotation-header-guide-button cvat-annotation-header-button'
                onClick={async (): Promise<void> => {
                    try {
                        Modal.info({
                            icon: null,
                            width: window.screen.availWidth * 0.5,
                            className: 'cvat-annotation-view-markdown-guide-modal',
                            content: (
                                <>
                                    <div>
                                        <p>
                                            <span>2D 检测分割标注规范</span>
                                            <span> - </span>
                                            <a
                                                href={`${jobInstance.annotationLaw.url}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                在线查看
                                            </a>
                                        </p>
                                    </div>
                                    <div>
                                        <p>
                                            <span>2D 检测分割审核流程</span>
                                            <span> - </span>
                                            <a
                                                href='https://doc.weixin.qq.com/doc/w3_AbIA3QZjANAo0kdI6KgTzqe7YYdQm'
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                在线查看
                                            </a>
                                        </p>
                                    </div>
                                </>
                            ),
                        });
                    } catch (error: any) {
                        notification.error({
                            message: '不能获取操作指南',
                            description: error.toString(),
                        });
                    }
                }}
            >
                <Icon component={InfoIcon} />
                操作指南
            </Button>
            <Button
                type='link'
                className='cvat-annotation-header-guide-button cvat-annotation-header-button'
                onClick={async (): Promise<void> => {
                    try {
                        Modal.info({
                            icon: null,
                            width: window.screen.availWidth * 0.5,
                            className: 'cvat-annotation-view-markdown-guide-modal',
                            content: (
                                <>
                                    <div>
                                        按 F1 键可快速查看可用的快捷键说明
                                    </div>
                                </>
                            ),
                        });
                    } catch (error: any) {
                        notification.error({
                            message: '不能获取快捷键位',
                            description: error.toString(),
                        });
                    }
                }}
            >
                <Icon component={ShortcutIcon} />
                快捷键位
            </Button>
            <Button
                type='link'
                className='cvat-annotation-header-fullscreen-button cvat-annotation-header-button'
                onClick={(): void => {
                    if (window.document.fullscreenEnabled) {
                        if (window.document.fullscreenElement) {
                            window.document.exitFullscreen();
                        } else {
                            window.document.documentElement.requestFullscreen();
                        }
                    }
                }}
            >
                <Icon component={FullscreenIcon} />
                全屏显示
            </Button>
            <Button
                type='link'
                className='cvat-annotation-header-info-button cvat-annotation-header-button'
                onClick={showStatistics}
            >
                <Icon component={AnalysisIcon} />
                标注分析
            </Button>
            <Button
                type='link'
                className={`cvat-annotation-header-filters-button cvat-annotation-header-button ${filters ?
                    'filters-armed' : ''
                }`}
                onClick={showFilters}
            >
                <Icon component={FilterIcon} />
                过滤
            </Button>
            <div>
                <Select
                    dropdownClassName='cvat-workspace-selector-dropdown'
                    className='cvat-workspace-selector'
                    onChange={changeWorkspace}
                    value={workspace}
                >
                    {Object.values(Workspace).map((ws) => {
                        if (jobInstance.dimension === DimensionType.DIMENSION_3D) {
                            if (ws === Workspace.STANDARD) {
                                return null;
                            }
                            return (
                                <Select.Option disabled={ws !== Workspace.STANDARD3D} key={ws} value={ws}>
                                    {GetWorkspaceChinese(ws)}
                                </Select.Option>
                            );
                        }
                        // 临时移除 TAG 标注模式
                        if (ws !== Workspace.STANDARD3D && ws !== Workspace.TAG_ANNOTATION) {
                            return (
                                <Select.Option key={ws} value={ws}>
                                    {GetWorkspaceChinese(ws)}
                                </Select.Option>
                            );
                        }
                        return null;
                    })}
                </Select>
            </div>
        </Col>
    );
}

export default React.memo(RightGroup);
